import { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { Smartphone, Signal, Wifi, BatteryFull, Timer } from "lucide-react";
import { Logo } from "./landing/Logo";

const EXP_URL =
  "exp://study-social-eat.preview.emergentagent.com?expo_go_prompt_device_auth=1&expo_go_device_auth_verification_uri_override=app.emergent.sh";

const CHARS = "01<>[]{}#$%&*+=/\\|xyzkZQ09";
const PREVIEW_SECONDS = 30 * 60;

const CodeTexture = () => {
  const text = useMemo(() => {
    let out = "";
    for (let i = 0; i < 5200; i++) {
      out += CHARS[Math.floor(Math.random() * CHARS.length)];
      if (Math.random() < 0.12) out += " ";
    }
    return out;
  }, []);
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 select-none overflow-hidden"
    >
      <pre className="whitespace-pre-wrap break-all font-jbmono text-[10px] leading-5 text-white opacity-[0.045]">
        {text}
      </pre>
    </div>
  );
};

const Countdown = () => {
  const [secs, setSecs] = useState(PREVIEW_SECONDS);
  useEffect(() => {
    const t = setInterval(() => setSecs((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);
  const mm = String(Math.floor(secs / 60)).padStart(2, "0");
  const ss = String(secs % 60).padStart(2, "0");
  const urgent = secs < 60;
  return (
    <span
      data-testid="expiry-countdown"
      className={`inline-flex items-center gap-1.5 font-jbmono font-bold tabular-nums ${
        urgent ? "animate-pulse text-red-400" : "text-[#A3E635]"
      }`}
    >
      <Timer className="h-3.5 w-3.5" />
      {mm}:{ss}
    </span>
  );
};

const IPhoneMock = ({ qr }) => (
  <div
    data-testid="iphone-mockup"
    className="w-[280px] rounded-[3rem] border border-white/10 bg-[#101012] p-2.5 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)] sm:w-[300px]"
  >
    <div className="overflow-hidden rounded-[2.5rem] bg-black">
      <div className="flex items-center justify-between px-7 pt-4 text-white">
        <span className="text-[11px] font-semibold tracking-wide">9:41</span>
        <span className="h-[18px] w-[72px] rounded-full bg-[#101012]" aria-hidden="true" />
        <span className="flex items-center gap-1">
          <Signal className="h-3 w-3" />
          <Wifi className="h-3 w-3" />
          <BatteryFull className="h-3.5 w-3.5" />
        </span>
      </div>
      <div className="flex flex-col items-center px-8 pb-12 pt-9">
        <div className="rounded-2xl bg-white p-3">
          {qr ? (
            <img
              src={qr}
              alt="QR code to open the app in Expo Go"
              data-testid="qr-code"
              className="h-40 w-40 sm:h-44 sm:w-44"
            />
          ) : (
            <div className="h-40 w-40 animate-pulse rounded-lg bg-neutral-200 sm:h-44 sm:w-44" />
          )}
        </div>
        <p
          data-testid="app-name"
          className="mt-6 text-[15px] font-bold tracking-tight text-white"
        >
          study-social-eat
        </p>
      </div>
    </div>
  </div>
);

export const SharePage = () => {
  const [qr, setQr] = useState(null);

  useEffect(() => {
    document.title = "FUNZ — Try the app on mobile";
    QRCode.toDataURL(EXP_URL, { width: 512, margin: 1 }).then(setQr).catch(() => {});
  }, []);

  return (
    <div
      data-testid="share-preview-page"
      className="relative flex min-h-screen flex-col bg-[#0A0A0A] text-white"
    >
      <CodeTexture />

      <header className="relative z-10 flex items-center justify-between px-6 py-6 sm:px-10">
        <a
          href="/"
          data-testid="funz-wordmark"
          className="flex items-center gap-2.5"
        >
          <Logo className="h-8 w-8" />
          <span className="font-display text-xl font-extrabold tracking-[0.18em] text-white">
            FUNZ
          </span>
        </a>
        <a
          href="https://emergent.sh"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="build-own-app-link"
          className="rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-neutral-200"
        >
          Build your own App
        </a>
      </header>

      <main className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 pb-24 pt-14 text-center">
        <h1 className="flex items-center gap-2.5 text-xl font-semibold tracking-tight text-white">
          <Smartphone className="h-5 w-5" />
          Try your app on mobile
        </h1>
        <p className="mt-3 max-w-md text-sm leading-relaxed text-neutral-400">
          Download Expo Go app and scan the QR preview to view and test the app on
          your phone.
        </p>

        <div className="mt-10">
          <IPhoneMock qr={qr} />
        </div>

        <div className="mt-9 flex w-full max-w-[300px] flex-col gap-3">
          <a
            href="/app"
            data-testid="open-web-app-button"
            className="flex h-12 items-center justify-center rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] text-sm font-bold text-white transition-transform hover:scale-[1.02]"
          >
            Open the full FUNZ app
          </a>
          <p className="-mt-1 text-center font-jbmono text-[10px] uppercase tracking-widest text-neutral-500">
            Same features as native — ChefRider, Rider Hub, everything · install via "Add to Home Screen"
          </p>
          <a
            href="https://expo.dev/go"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="download-expo-go-button"
            className="flex h-12 items-center justify-center rounded-full border border-white/10 bg-[#1C1C1E] text-sm font-semibold text-white transition-colors hover:bg-[#2A2A2E]"
          >
            Download Expo Go
          </a>
          <a
            href={EXP_URL}
            data-testid="open-in-expo-go-button"
            className="flex h-12 items-center justify-center rounded-full bg-white text-sm font-semibold text-black transition-colors hover:bg-neutral-200"
          >
            Open in Expo Go
          </a>
        </div>

        <div
          data-testid="preview-expiry-note"
          className="mt-9 w-full max-w-md rounded-2xl border border-white/5 bg-[#111113] px-6 py-4 sm:border-0 sm:bg-transparent sm:p-0"
        >
          <p className="flex items-center justify-center gap-2 text-sm font-medium text-neutral-300">
            This FUNZ preview expires in <Countdown />
          </p>
          <p className="mt-1 text-sm leading-relaxed text-neutral-500">
            Deploy your app to get a permanent link that never expires
          </p>
        </div>

        <a
          href="https://emergent.sh"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="made-with-emergent"
          className="mt-8 flex items-center gap-2 self-end rounded-full bg-white px-4 py-2.5 text-sm font-semibold text-black shadow-lg transition-colors hover:bg-neutral-200 sm:fixed sm:bottom-5 sm:right-5 sm:z-20 sm:mt-0"
        >
          <img
            src="https://assets.emergent.sh/emergent-ico.png"
            alt=""
            className="h-4 w-4"
          />
          Made with Emergent
        </a>
      </main>
    </div>
  );
};
